import { useEffect, useMemo, useRef, useState } from "react";

import {
  ApiError,
  uploadRejection,
  type CustomPostPreview,
  type EditablePost,
  type MeResponse,
  type PersonalButtonSet,
  type PostConfiguration,
  type PostKind,
  type PostPreviewResponse,
  type PriceSource,
  type PostReplacementResponse,
  type PostReplacementStatus,
  type ReplacePublishedPostRequest,
  type RetryPostSyncRequest,
  type UpdatePostRequest,
  type UpdatePostResponse,
  type UploadedMedia
} from "../../api/client";
import { ButtonSetPicker } from "../button-sets/ButtonSetPicker";
import { CustomEmojiTextEditor } from "../custom-emoji/CustomEmojiTextEditor";
import { ButtonLayoutEditor } from "../composer/ButtonLayoutEditor";
import { canonicalButtonRows, type ButtonLayoutLimits, type EditableButtonRow } from "../composer/button-layout-model";
import { TelegramPreview } from "../composer/TelegramPreview";
import { CategorySelectField } from "../price/CategoryPicker";
import { buildCategoryOptions } from "../price/category-model";
import { PostLiveSyncStatus } from "./PostLiveSyncStatus";

export type PostEditorApi = {
  loadPost: (kind: PostKind, id: string, signal?: AbortSignal) => Promise<EditablePost>;
  loadPostMedia: (kind: PostKind, id: string, index: number, signal?: AbortSignal) => Promise<Blob>;
  uploadMedia: (file: File) => Promise<UploadedMedia>;
  loadButtonSets: () => Promise<PersonalButtonSet[]>;
  loadPrice: (signal?: AbortSignal) => Promise<PriceSource>;
  preview: (body: Record<string, unknown>) => Promise<PostPreviewResponse>;
  updatePost: (kind: PostKind, id: string, body: UpdatePostRequest, signal?: AbortSignal) => Promise<UpdatePostResponse>;
  retryPostSync: (kind: PostKind, id: string, body: RetryPostSyncRequest, signal?: AbortSignal) => Promise<UpdatePostResponse>;
  replacePublishedPost: (kind: PostKind, id: string, body: ReplacePublishedPostRequest, signal?: AbortSignal) => Promise<PostReplacementResponse>;
  loadReplacementStatus: (kind: PostKind, id: string, jobId: number, signal?: AbortSignal) => Promise<PostReplacementStatus>;
};

const safeError = (error: unknown) => {
  if (error instanceof ApiError && error.code === "REVISION_CONFLICT") {
    return "Пост уже изменён в другой сессии. Загрузите свежую версию.";
  }
  if (error instanceof ApiError && error.code === "PREVIEW_STALE") {
    return "Предпросмотр устарел. Получите его ещё раз перед сохранением.";
  }
  if (error instanceof ApiError && error.code === "TARGET_UNAVAILABLE") {
    return "Группа или права бота требуют проверки перед сохранением.";
  }
  return "Не удалось сохранить изменения. Будущие публикации пока не изменены.";
};

const asPreviewMedia = (
  post: EditablePost,
  localPreviewUrls: Record<number, string>
): UploadedMedia[] => post.mediaItems.map((media, index) => ({
  mediaType: media.mediaType,
  mediaFileId: media.mediaFileId,
  fileName: media.fileName ?? `Медиа ${index + 1}`,
  contentType: media.contentType ?? "application/octet-stream",
  size: media.size ?? 0,
  ...(media.width !== undefined ? { width: media.width } : {}),
  ...(media.height !== undefined ? { height: media.height } : {}),
  ...(media.duration !== undefined ? { duration: media.duration } : {}),
  ...(localPreviewUrls[index] ? { localPreviewUrl: localPreviewUrls[index] } : {})
}));

const configurationFor = (
  post: EditablePost,
  capabilities: MeResponse["capabilities"],
  buttonLimits: ButtonLayoutLimits
): PostConfiguration => {
  const buttonRows = canonicalButtonRows(post.buttonRows as EditableButtonRow[], buttonLimits);
  if (post.kind === "custom") {
    return {
      chatId: post.chatId,
      text: post.text,
      overflowNoteEnabled: post.overflowNoteEnabled,
      scheduleType: post.scheduleType,
      intervalMinutes: post.scheduleType === "interval" ? post.intervalMinutes : null,
      dailyTime: post.scheduleType === "interval" ? null : post.dailyTime,
      enabled: post.enabled,
      mediaItems: post.mediaItems,
      buttonRows,
      ...(post.personalButtonSetId !== null
        ? { personalButtonSetId: post.personalButtonSetId }
        : {}),
      deliveryPreference: post.deliveryPreference,
      layout: post.layout,
      buttonAlignment: post.buttonAlignment,
      richVideoOverrideAck: post.richVideoOverrideAck,
      ...(capabilities.admin ? {
        mirrorToMax: post.mirrorToMax,
        ...(post.urlLinkSetId !== null ? { urlLinkSetId: post.urlLinkSetId } : {})
      } : {})
    };
  }
  return {
    chatId: post.chatId,
    categories: post.categories,
    markupType: post.markupType,
    markupValue: post.markupValue,
    markupRound: post.markupRound,
    markupRoundMode: post.markupRoundMode ?? "none",
    title: post.title,
    headerParagraph: post.headerParagraph,
    contact: post.contact,
    extraInfo: post.extraInfo,
    promoEnabled: post.promoEnabled,
    overflowNoteEnabled: post.overflowNoteEnabled,
    postOnChangeOnly: post.postOnChangeOnly,
    editOnChangeOnly: post.editOnChangeOnly,
    scheduleType: post.scheduleType,
    intervalMinutes: post.scheduleType === "interval" ? post.intervalMinutes : null,
    dailyTime: post.scheduleType === "daily" ? post.dailyTime : null,
    enabled: post.enabled,
    mediaItems: post.mediaItems,
    buttonRows,
    deliveryMode: post.deliveryMode,
    ...(post.personalButtonSetId !== null
      ? { personalButtonSetId: post.personalButtonSetId }
      : {}),
    ...(capabilities.admin && post.urlLinkSetId !== null
      ? { urlLinkSetId: post.urlLinkSetId }
      : {})
  };
};

export function PostEditor({
  kind,
  postId,
  capabilities,
  buttonLimits,
  api,
  onClose
}: {
  kind: PostKind;
  postId: string;
  capabilities: MeResponse["capabilities"];
  buttonLimits: ButtonLayoutLimits;
  api: PostEditorApi;
  onClose: () => void;
}) {
  const [post, setPost] = useState<EditablePost | null>(null);
  const [draft, setDraft] = useState<EditablePost | null>(null);
  const [loadingError, setLoadingError] = useState(false);
  const [preview, setPreview] = useState<PostPreviewResponse | null>(null);
  const [previewSessionId, setPreviewSessionId] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [replacementConfirmOpen, setReplacementConfirmOpen] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [replacementJob, setReplacementJob] = useState<PostReplacementResponse | null>(null);
  const [replacementNotice, setReplacementNotice] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [buttonSets, setButtonSets] = useState<PersonalButtonSet[]>([]);
  const [buttonSetPickerOpen, setButtonSetPickerOpen] = useState(false);
  const [buttonSetStatus, setButtonSetStatus] = useState<string | null>(null);
  const [mediaPreviewUrls, setMediaPreviewUrls] = useState<Record<number, string>>({});
  const [mediaPreviewFailed, setMediaPreviewFailed] = useState(false);
  const [mediaBusy, setMediaBusy] = useState(false);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [replaceMediaIndex, setReplaceMediaIndex] = useState<number | null>(null);
  const mediaInputRef = useRef<HTMLInputElement | null>(null);
  const uploadedPreviewUrlsRef = useRef<Record<string, string>>({});
  const [priceSource, setPriceSource] = useState<PriceSource | null>(null);
  const [priceLoading, setPriceLoading] = useState(kind === "price");
  const [priceError, setPriceError] = useState(false);
  const [priceReloadToken, setPriceReloadToken] = useState(0);
  const linkedButtonSetName = draft?.personalButtonSetId
    ? buttonSets.find((item) => item.id === draft.personalButtonSetId)?.name ?? null
    : null;
  const displayedButtonSetStatus = buttonSetStatus ?? (draft?.personalButtonSetId
    ? linkedButtonSetName
      ? `Связан с набором «${linkedButtonSetName}»`
      : "Связан с личным набором"
    : null);
  const saveRequestId = useRef<string | null>(null);
  const saveControllerRef = useRef<AbortController | null>(null);
  const previewRequestTokenRef = useRef(0);

  const applyLoaded = (value: EditablePost) => {
    setPost(value);
    setDraft(value);
    setLoadingError(false);
  };

  useEffect(() => {
    const controller = new AbortController();
    setLoadingError(false);
    void api.loadPost(kind, postId, controller.signal).then(
      applyLoaded,
      () => { if (!controller.signal.aborted) setLoadingError(true); }
    );
    return () => controller.abort();
  }, [api, kind, postId]);

  useEffect(() => {
    if (kind !== "price") return;
    const controller = new AbortController();
    setPriceLoading(true);
    setPriceError(false);
    void api.loadPrice(controller.signal).then(
      (source) => {
        if (controller.signal.aborted) return;
        setPriceSource(source);
        setPriceLoading(false);
      },
      () => {
        if (controller.signal.aborted) return;
        setPriceError(true);
        setPriceLoading(false);
      }
    );
    return () => controller.abort();
  }, [api, kind, priceReloadToken]);

  useEffect(() => () => {
    saveControllerRef.current?.abort();
    previewRequestTokenRef.current += 1;
    if (typeof URL.revokeObjectURL === "function") {
      Object.values(uploadedPreviewUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
    }
    uploadedPreviewUrlsRef.current = {};
  }, []);

  useEffect(() => {
    let active = true;
    void api.loadButtonSets().then(
      (items) => { if (active) setButtonSets(items); },
      () => { if (active) setButtonSets([]); }
    );
    return () => { active = false; };
  }, [api]);

  const mediaSignature = draft?.mediaItems
    .map((media) => `${media.mediaType}:${media.mediaFileId}`)
    .join("|") ?? "";

  useEffect(() => {
    const mediaItems = draft?.mediaItems ?? [];
    const controller = new AbortController();
    const createdUrls: string[] = [];
    let active = true;
    setMediaPreviewUrls(Object.fromEntries(mediaItems.flatMap((media, index) => {
      const localUrl = uploadedPreviewUrlsRef.current[media.mediaFileId];
      return localUrl ? [[index, localUrl] as const] : [];
    })));
    setMediaPreviewFailed(false);
    void (async () => {
      for (const [index, media] of mediaItems.entries()) {
        if (media.mediaType === "document") continue;
        if (uploadedPreviewUrlsRef.current[media.mediaFileId]) continue;
        try {
          const blob = await api.loadPostMedia(kind, postId, index, controller.signal);
          if (!active || typeof URL.createObjectURL !== "function") return;
          const url = URL.createObjectURL(blob);
          createdUrls.push(url);
          setMediaPreviewUrls((current) => ({ ...current, [index]: url }));
        } catch {
          if (!controller.signal.aborted && active) setMediaPreviewFailed(true);
        }
      }
    })();
    return () => {
      active = false;
      controller.abort();
      if (typeof URL.revokeObjectURL === "function") {
        createdUrls.forEach((url) => URL.revokeObjectURL(url));
      }
    };
  }, [api, kind, mediaSignature, postId]);

  useEffect(() => {
    if (!post || post.liveSyncState !== "pending") return;
    let elapsed = 0;
    let running = false;
    const timer = window.setInterval(() => {
      elapsed += 2_000;
      if (elapsed >= 60_000) {
        window.clearInterval(timer);
        return;
      }
      if (running) return;
      running = true;
      void api.loadPost(kind, postId).then((latest) => {
        setPost(latest);
        if (latest.liveSyncState !== "pending") window.clearInterval(timer);
      }, () => undefined).finally(() => { running = false; });
    }, 2_000);
    return () => window.clearInterval(timer);
  }, [api, kind, post?.liveSyncState, post?.revision, postId]);

  useEffect(() => {
    if (!replacementJob || replacementJob.state === "completed") return;
    let elapsed = 0;
    let running = false;
    const controller = new AbortController();
    const timer = window.setInterval(() => {
      elapsed += 2_000;
      if (elapsed >= 60_000) {
        window.clearInterval(timer);
        setReplacementNotice("Замена выполняется дольше обычного. Проверьте статус позже.");
        return;
      }
      if (running) return;
      running = true;
      void api.loadReplacementStatus(kind, postId, replacementJob.jobId, controller.signal).then(
        async (status) => {
          setReplacementJob((current) => current ? { ...current, state: status.state } : current);
          if (status.state === "completed") {
            window.clearInterval(timer);
            const latest = await api.loadPost(kind, postId, controller.signal);
            applyLoaded(latest);
            setReplacementNotice("Опубликованное сообщение заменено и синхронизировано.");
          } else if (status.state === "needs_review") {
            window.clearInterval(timer);
            setReplacementNotice("Telegram мог создать новое сообщение. Проверьте группу: старое сообщение может потребовать ручного удаления.");
          } else if (status.state === "failed" || status.state === "stale") {
            window.clearInterval(timer);
            setReplacementNotice("Замену не удалось завершить. Старое сообщение не удалено.");
          }
        },
        () => undefined
      ).finally(() => { running = false; });
    }, 2_000);
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [api, kind, postId, replacementJob?.jobId]);

  const categoryOptions = useMemo(
    () => priceSource ? buildCategoryOptions(priceSource, draft?.kind === "price" ? draft.categories : []) : [],
    [draft, priceSource]
  );

  const changeDraft = (patch: Partial<EditablePost>) => {
    previewRequestTokenRef.current += 1;
    setDraft((current) => current ? ({ ...current, ...patch } as EditablePost) : current);
    setPreview(null);
    setPreviewSessionId(null);
    setPreviewing(false);
    setSaved(false);
    setError(null);
    saveRequestId.current = null;
  };

  const revokeUploadedPreview = (mediaFileId: string) => {
    const localUrl = uploadedPreviewUrlsRef.current[mediaFileId];
    if (localUrl && typeof URL.revokeObjectURL === "function") URL.revokeObjectURL(localUrl);
    delete uploadedPreviewUrlsRef.current[mediaFileId];
  };

  const chooseMedia = (index: number | null) => {
    setReplaceMediaIndex(index);
    setMediaError(null);
    mediaInputRef.current?.click();
  };

  const uploadSelectedMedia = async (file: File | undefined) => {
    if (!file || !draft) return;
    const rejection = uploadRejection(file);
    if (rejection) {
      setMediaError(rejection);
      setReplaceMediaIndex(null);
      if (mediaInputRef.current) mediaInputRef.current.value = "";
      return;
    }
    const targetIndex = replaceMediaIndex;
    setMediaBusy(true);
    setMediaError(null);
    try {
      const uploaded = await api.uploadMedia(file);
      const nextMedia = [...draft.mediaItems];
      if (targetIndex === null) {
        nextMedia.push(uploaded);
      } else {
        const previous = nextMedia[targetIndex];
        if (previous) revokeUploadedPreview(previous.mediaFileId);
        nextMedia[targetIndex] = uploaded;
      }
      if (typeof URL.createObjectURL === "function") {
        uploadedPreviewUrlsRef.current[uploaded.mediaFileId] = URL.createObjectURL(file);
      }
      changeDraft({
        mediaItems: nextMedia,
        ...(uploaded.mediaType === "video" ? { richVideoOverrideAck: false } : {})
      } as Partial<EditablePost>);
    } catch (error) {
      setMediaError(error instanceof ApiError
        ? error.message
        : "Не удалось загрузить медиафайл. Попробуйте ещё раз.");
    } finally {
      setReplaceMediaIndex(null);
      setMediaBusy(false);
      if (mediaInputRef.current) mediaInputRef.current.value = "";
    }
  };

  const removeMedia = (index: number) => {
    if (!draft) return;
    const previous = draft.mediaItems[index];
    if (previous) revokeUploadedPreview(previous.mediaFileId);
    changeDraft({
      mediaItems: draft.mediaItems.filter((_, itemIndex) => itemIndex !== index),
      richVideoOverrideAck: false
    } as Partial<EditablePost>);
  };

  const requestPreview = async () => {
    if (!draft) return;
    const requestToken = previewRequestTokenRef.current + 1;
    previewRequestTokenRef.current = requestToken;
    setPreviewing(true);
    setError(null);
    try {
      const result = await api.preview({ kind, ...configurationFor(draft, capabilities, buttonLimits) });
      if (previewRequestTokenRef.current !== requestToken) return;
      const sessionId = typeof result.previewSessionId === "string" ? result.previewSessionId : null;
      if (!sessionId) throw new Error("preview session missing");
      setPreview(result);
      setPreviewSessionId(sessionId);
      saveRequestId.current = null;
    } catch {
      if (previewRequestTokenRef.current !== requestToken) return;
      setPreview(null);
      setPreviewSessionId(null);
      setError("Не удалось получить предпросмотр. Попробуйте ещё раз.");
    } finally {
      if (previewRequestTokenRef.current === requestToken) setPreviewing(false);
    }
  };

  const save = async () => {
    if (!draft || !previewSessionId) return;
    setSaving(true);
    setError(null);
    const requestId = saveRequestId.current ?? crypto.randomUUID();
    saveRequestId.current = requestId;
    const controller = new AbortController();
    saveControllerRef.current = controller;
    try {
      const updated = await api.updatePost(kind, postId, {
        requestId,
        expectedRevision: draft.revision,
        previewSessionId,
        syncLastPublished: true,
        configuration: configurationFor(draft, capabilities, buttonLimits)
      }, controller.signal);
      setPost(updated);
      setDraft(updated);
      setSaved(true);
      setPreview(null);
      setPreviewSessionId(null);
      saveRequestId.current = null;
    } catch (caught) {
      setError(safeError(caught));
      if (caught instanceof ApiError && (caught.code === "PREVIEW_STALE" || caught.code === "REVISION_CONFLICT")) {
        setPreview(null);
        setPreviewSessionId(null);
        saveRequestId.current = null;
      }
    } finally {
      if (saveControllerRef.current === controller) saveControllerRef.current = null;
      setSaving(false);
    }
  };

  const retry = async () => {
    if (!post || post.liveSyncState !== "failed") return;
    setRetrying(true);
    setError(null);
    try {
      const updated = await api.retryPostSync(kind, postId, {
        requestId: crypto.randomUUID(),
        expectedRevision: post.revision
      });
      setPost(updated);
    } catch {
      setError("Повтор не запущен. Обновите пост и проверьте его статус.");
    } finally {
      setRetrying(false);
    }
  };

  const replacePublished = async () => {
    if (!post || replacing) return;
    setReplacing(true);
    setError(null);
    setReplacementNotice(null);
    try {
      const result = await api.replacePublishedPost(kind, postId, {
        requestId: crypto.randomUUID(),
        expectedRevision: post.revision,
        confirmReplacement: true
      });
      setReplacementJob(result);
      setReplacementConfirmOpen(false);
      setReplacementNotice("Новое сообщение создаётся. Старое будет удалено только после подтверждённой отправки.");
    } catch {
      setError("Замену не удалось запустить. Старое сообщение осталось без изменений.");
    } finally {
      setReplacing(false);
    }
  };

  if (loadingError) {
    return <section className="post-editor fullscreen-state"><h1>Не удалось загрузить пост</h1><button type="button" onClick={onClose}>Назад</button></section>;
  }
  if (!post || !draft) return <div className="skeleton list-skeleton" aria-label="Загрузка поста" />;

  const scheduleOptions = draft.kind === "custom"
    ? [["planned", "Однократно"], ["interval", "По интервалу"], ["daily", "Ежедневно"]]
    : [["now", "Сейчас"], ["interval", "По интервалу"], ["daily", "Ежедневно"]];
  const postButtonCount = draft.buttonRows.reduce(
    (total, row) => total + row.buttons.length,
    0
  );

  return (
    <section className="post-editor composer" aria-label="Редактирование публикации">
      <header className="composer-header">
        <button type="button" aria-label="Закрыть редактор" onClick={onClose}>←</button>
        <div><p className="eyebrow">Пост · ревизия {draft.revision}</p><h1>Редактирование</h1></div>
        <span />
      </header>

      <PostLiveSyncStatus
        post={post}
        retrying={retrying}
        replacing={replacing || replacementJob?.state === "pending" || replacementJob?.state === "claimed" || replacementJob?.state === "delivered"}
        onRetry={() => void retry()}
        onReplace={() => setReplacementConfirmOpen(true)}
      />
      {replacementNotice && <p className="replacement-notice" role="status">{replacementNotice}</p>}
      {replacementConfirmOpen && (
        <div className="post-replacement-backdrop">
          <section className="post-replacement-confirm" role="dialog" aria-modal="true" aria-label="Замена опубликованного сообщения">
            <h2>Заменить сообщение в Telegram?</h2>
            <p>Сначала бот создаст новое сообщение и только после подтверждённой отправки удалит старое.</p>
            <p>Новое сообщение получит новую ссылку и новый ID. Реакции и просмотры старого сообщения перенести нельзя.</p>
            <div>
              <button type="button" disabled={replacing} onClick={() => setReplacementConfirmOpen(false)}>Отмена</button>
              <button className="danger-action" type="button" disabled={replacing} onClick={() => void replacePublished()}>{replacing ? "Создаём…" : "Создать новое и удалить старое"}</button>
            </div>
          </section>
        </div>
      )}
      {saved && <p className="save-success" role="status">Изменения для следующих публикаций сохранены</p>}
      {error && <p className="preview-error" role="alert">{error}</p>}

      <div className="post-editor-form field-stack">
        {draft.kind === "custom" ? (
          <>
            <CustomEmojiTextEditor label="Текст поста" rows={8} value={draft.text} onChange={(text) => changeDraft({ text } as Partial<EditablePost>)} />
            <label className="switch-row"><input aria-label="Добавлять приписку про PDF при переполнении" type="checkbox" checked={draft.overflowNoteEnabled} onChange={(event) => changeDraft({ overflowNoteEnabled: event.target.checked } as Partial<EditablePost>)} /><span><strong>Приписка про PDF при переполнении</strong></span></label>
            <fieldset className="field-stack"><legend>Формат Telegram</legend>{(["auto", "legacy", "rich"] as const).map((value) => <label className="choice-row" key={value}><input type="radio" name="post-format" checked={draft.deliveryPreference === value} onChange={() => changeDraft({ deliveryPreference: value } as Partial<EditablePost>)} /><span><strong>{value === "auto" ? "Авто" : value === "legacy" ? "Обычный" : "Новый Rich"}</strong></span></label>)}</fieldset>
            {capabilities.admin && <label className="switch-row admin-only"><input type="checkbox" checked={draft.mirrorToMax} onChange={(event) => changeDraft({ mirrorToMax: event.target.checked } as Partial<EditablePost>)} /><span><strong>Пересылать в MAX</strong><small>Только для администратора</small></span></label>}
          </>
        ) : (
          <>
            <CustomEmojiTextEditor label="Заголовок поста" rows={2} value={draft.title ?? ""} onChange={(title) => changeDraft({ title } as Partial<EditablePost>)} />
            <CustomEmojiTextEditor label="Абзац после заголовка" rows={4} value={draft.headerParagraph ?? ""} onChange={(headerParagraph) => changeDraft({ headerParagraph } as Partial<EditablePost>)} />
            <CategorySelectField
              options={categoryOptions}
              value={draft.categories}
              loading={priceLoading}
              error={priceError}
              onRetry={() => setPriceReloadToken((value) => value + 1)}
              onApply={(categories) => changeDraft({ categories } as Partial<EditablePost>)}
            />
            <label className="field"><span>Тип наценки</span><select aria-label="Тип наценки" value={draft.markupType} onChange={(event) => changeDraft({ markupType: event.target.value as "none" | "fixed" | "percent" } as Partial<EditablePost>)}><option value="none">Без наценки</option><option value="fixed">Фиксированная</option><option value="percent">Процент</option></select></label>
            {draft.markupType !== "none" && <label className="field"><span>Значение наценки</span><input aria-label="Значение наценки" type="number" value={draft.markupValue} onChange={(event) => changeDraft({ markupValue: Number(event.target.value) } as Partial<EditablePost>)} /></label>}
            {draft.markupType === "percent" && <label className="field"><span>Округление наценки</span><select aria-label="Округление наценки" value={draft.markupRoundMode ?? "none"} onChange={(event) => { const markupRoundMode = event.target.value; changeDraft({ markupRoundMode, markupRound: markupRoundMode !== "none" } as Partial<EditablePost>); }}><option value="none">Не округлять</option><option value="int">До целого</option><option value="hundred_up">Вверх до 100</option><option value="hundred_down">Вниз до 100</option></select></label>}
            <CustomEmojiTextEditor label="Контакт для заказа" rows={2} value={draft.contact ?? ""} onChange={(contact) => changeDraft({ contact } as Partial<EditablePost>)} />
            <CustomEmojiTextEditor label="Инфо-блок" rows={4} value={draft.extraInfo ?? ""} onChange={(extraInfo) => changeDraft({ extraInfo } as Partial<EditablePost>)} />
            <label className="switch-row"><input aria-label="Показывать промо-строку" type="checkbox" checked={draft.promoEnabled} onChange={(event) => changeDraft({ promoEnabled: event.target.checked } as Partial<EditablePost>)} /><span><strong>Показывать промо-строку</strong></span></label>
            <label className="switch-row"><input aria-label="Добавлять приписку про PDF при переполнении" type="checkbox" checked={draft.overflowNoteEnabled} onChange={(event) => changeDraft({ overflowNoteEnabled: event.target.checked } as Partial<EditablePost>)} /><span><strong>Приписка про PDF при переполнении</strong></span></label>
            <label className="field"><span>Формат</span><select aria-label="Формат прайс-поста" value={draft.deliveryMode} onChange={(event) => changeDraft({ deliveryMode: event.target.value as "legacy" | "rich" } as Partial<EditablePost>)}><option value="legacy">Обычный</option><option value="rich">Новый Rich</option></select></label>
            <label className="switch-row"><input type="checkbox" checked={draft.postOnChangeOnly} onChange={(event) => changeDraft({ postOnChangeOnly: event.target.checked } as Partial<EditablePost>)} /><span><strong>Только при изменении прайса</strong></span></label>
            <label className="switch-row"><input type="checkbox" checked={draft.editOnChangeOnly} onChange={(event) => changeDraft({ editOnChangeOnly: event.target.checked } as Partial<EditablePost>)} /><span><strong>Авторедактируемый пост</strong></span></label>
          </>
        )}

        <section className="personal-button-set-action">
          <div><strong>Готовые кнопки</strong><small>Свяжите набор для автоматических обновлений или создайте независимую копию.</small></div>
          <button type="button" onClick={() => setButtonSetPickerOpen(true)}>Выбрать из моих наборов</button>
          {displayedButtonSetStatus && <p className="save-success" role="status" aria-label="Статус набора кнопок">{displayedButtonSetStatus}</p>}
        </section>
        <p className="helper-copy">Кнопки поста · {draft.buttonRows.length} рядов · {postButtonCount} кнопок</p>
        <ButtonLayoutEditor rows={draft.buttonRows as EditableButtonRow[]} onChange={(buttonRows) => { setButtonSetStatus(null); changeDraft({ buttonRows, urlLinkSetId: null, personalButtonSetId: null } as Partial<EditablePost>); }} richEnabled={capabilities.richPosts} limits={buttonLimits} />

        <label className="field"><span>Расписание</span><select aria-label="Расписание" value={draft.scheduleType} onChange={(event) => changeDraft({ scheduleType: event.target.value } as Partial<EditablePost>)}>{scheduleOptions.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label>
        {draft.scheduleType === "interval" && <label className="field"><span>Интервал, минут</span><input aria-label="Интервал, минут" type="number" min="1" value={draft.intervalMinutes ?? 60} onChange={(event) => changeDraft({ intervalMinutes: Number(event.target.value) } as Partial<EditablePost>)} /></label>}
        {draft.scheduleType !== "interval" && <label className="field"><span>{draft.scheduleType === "daily" ? "Время, мск" : "Дата и время"}</span><input aria-label="Время публикации" value={draft.dailyTime ?? ""} onChange={(event) => changeDraft({ dailyTime: event.target.value } as Partial<EditablePost>)} /></label>}
        <label className="switch-row"><input type="checkbox" checked={draft.enabled} onChange={(event) => changeDraft({ enabled: event.target.checked } as Partial<EditablePost>)} /><span><strong>Публикации включены</strong></span></label>
        <section className="stored-post-media" aria-label="Сохранённые медиа поста">
          <div className="stored-post-media-heading"><div><strong>Медиа поста · {draft.mediaItems.length}</strong><small>Можно заменить, удалить или добавить файл.</small></div><button type="button" disabled={mediaBusy || draft.mediaItems.length >= 10} onClick={() => chooseMedia(null)}>{mediaBusy ? "Загрузка…" : "+ Добавить"}</button></div>
          <input ref={mediaInputRef} className="visually-hidden" aria-label="Новый медиафайл" type="file" accept="image/jpeg,image/png,image/webp,video/*,application/pdf" onChange={(event) => void uploadSelectedMedia(event.currentTarget.files?.[0])} />
          {draft.mediaItems.length === 0 && <p className="helper-copy">Медиа не добавлено</p>}
          {draft.mediaItems.length > 0 && <div className="stored-post-media-list">{draft.mediaItems.map((media, index) => {
            const localUrl = uploadedPreviewUrlsRef.current[media.mediaFileId] ?? mediaPreviewUrls[index];
            const name = media.fileName ?? `Медиа ${index + 1}`;
            let preview;
            if (media.mediaType === "photo" && localUrl) {
              preview = <img src={localUrl} alt={`Фото ${name}`} />;
            } else if (media.mediaType === "video" && localUrl) {
              preview = <video src={localUrl} aria-label={`Видео ${name}`} controls playsInline preload="metadata" />;
            } else {
              const label = `${media.mediaType === "photo" ? "Фото" : media.mediaType === "video" ? "Видео" : "Документ"} ${name}`;
              preview = <div className="stored-post-media-placeholder" role={media.mediaType === "document" ? "img" : "status"} aria-label={media.mediaType === "document" ? label : `Загрузка ${label}`}><span>{media.mediaType === "video" ? "▶" : media.mediaType === "document" ? "📄" : "▣"}</span><small>{name}</small></div>;
            }
            return <article className="stored-post-media-item" key={media.mediaFileId}>{preview}<div><button type="button" disabled={mediaBusy} aria-label={`Заменить ${name}`} onClick={() => chooseMedia(index)}>Заменить</button><button type="button" disabled={mediaBusy} aria-label={`Удалить ${name}`} onClick={() => removeMedia(index)}>Удалить</button></div></article>;
          })}</div>}
          {mediaPreviewFailed && <p className="helper-copy">Часть превью не загрузилась, но файлы сохранены в посте.</p>}
          {mediaError && <p className="preview-error" role="alert">{mediaError}</p>}
        </section>

        <div className="post-editor-actions">
          <button type="button" disabled={previewing || saving || mediaBusy} onClick={() => void requestPreview()}>{previewing ? "Готовим…" : "Предпросмотр"}</button>
          <button className="primary-button" type="button" disabled={!previewSessionId || previewing || saving || mediaBusy} onClick={() => void save()}>{saving ? "Сохраняем…" : "Сохранить изменения"}</button>
        </div>
        {preview && <div className="preview-card"><strong>Предпросмотр готов</strong><TelegramPreview display="inline" preview={preview as CustomPostPreview} mediaItems={asPreviewMedia(draft, mediaPreviewUrls)} /></div>}
      </div>
      {buttonSetPickerOpen && <ButtonSetPicker
        sets={buttonSets}
        currentRows={draft.buttonRows as EditableButtonRow[]}
        onLink={(item) => {
          changeDraft({ buttonRows: item.buttonRows, urlLinkSetId: null, personalButtonSetId: item.id } as Partial<EditablePost>);
          setButtonSetStatus(`Связан с набором «${item.name}»`);
          setButtonSetPickerOpen(false);
        }}
        onCopy={(buttonRows) => {
          changeDraft({ buttonRows, urlLinkSetId: null, personalButtonSetId: null } as Partial<EditablePost>);
          setButtonSetStatus("Набор скопирован в пост");
          setButtonSetPickerOpen(false);
        }}
        onClose={() => setButtonSetPickerOpen(false)}
      />}
    </section>
  );
}
